import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { Injector, NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { AuthConfig, OAuthModule, OAuthModuleConfig, OAuthStorage } from 'angular-oauth2-oidc';
import { storeFreeze } from 'ngrx-store-freeze';
import { environment } from '../environments/environment';
import { ApiModule } from './api/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppGuard } from './app.guard';
import { APP_CONFIG } from './app.models';
import { reducers } from './app.reducer';
import { AuthService } from './auth.service';
import { BaseModule } from './base/base.module';
import { RouterEffects } from './router/router.effects';
import { CustomSerializer } from './router/router.serializer';
import { ResponseInterceptor } from './shared/http/http-response-interceptor';
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { AlertifyModule } from '@liebherr/old-sage-ui';

// TODO add action logger
// export function storeLoggerFactory(reducer: ActionReducer<AppState>): any {
//   return storeLogger({
//     collapsed: true,
//   })(reducer);
// }
export function authAppInitializerFactory(authService: AuthService): () => Promise<void> {
    return () => authService.runInitialLoginSequence();
}

export function setupOAuthModule(injector: Injector): OAuthModuleConfig {
    const config = injector.get(APP_CONFIG);
    return {
        resourceServer: {
            allowedUrls: [config.coreUrl],
            sendAccessToken: true,
        },
    };
}

export function setupAuthConfig(injector: Injector): AuthConfig {
    const config = injector.get(APP_CONFIG);
    const baseHref = injector.get(APP_BASE_HREF);
    return {
        issuer: config.oidc_issuer,
        clientId: config.oidc_clientId,
        responseType: 'code',
        scope: 'openid profile email roles',
        showDebugInformation: true,
        redirectUri: window.location.origin + baseHref,
        // TODO remove for production!
        requireHttps: false,
    };
}

export function storageFactory(): OAuthStorage {
    return localStorage;
}

export const metaReducers = environment.production ? [] : [storeFreeze];

const initialState = {
    router: {
        state: {
            url: window.location.pathname,
            params: {},
            queryParams: {},
        },
        navigationId: 0,
    },
};

@NgModule({
    declarations: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserModule,
        environment.e2e ? NoopAnimationsModule : BrowserAnimationsModule,
        HttpClientModule,
        BaseModule,
        StoreModule.forRoot(reducers, {
            metaReducers,
            initialState,
        }),
        EffectsModule.forRoot([RouterEffects]),
        OAuthModule.forRoot(),
        AlertifyModule,
        ApiModule,
        StoreRouterConnectingModule.forRoot({
            stateKey: 'router',
            serializer: CustomSerializer,
            navigationActionTiming: NavigationActionTiming.PreActivation,
        }),
        TranslocoRootModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            multi: true,
            useClass: ResponseInterceptor,
        },
        {
            provide: APP_BASE_HREF,
            useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
            deps: [PlatformLocation],
        },
        AppGuard,
        {
            provide: APP_INITIALIZER,
            useFactory: authAppInitializerFactory,
            deps: [AuthService],
            multi: true,
        },
        { provide: AuthConfig, useFactory: setupAuthConfig, deps: [Injector] },
        {
            provide: OAuthModuleConfig,
            useFactory: setupOAuthModule,
            deps: [Injector],
        },
        { provide: OAuthStorage, useFactory: storageFactory },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
